// src/pages/ContactSupport.jsx
import React, { useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { db } from "../firebase";

export default function ContactSupport() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "question",
    orderRef: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", text: "" });

  const supportEmail = "progroupapps@gmail.com";
  const supportPhone = "+22890678675";
  const whatsappNumber = "22890678675";

  const whatsappLink = useMemo(() => {
    const text = encodeURIComponent(
      "Bonjour GoShop, j’ai besoin d’aide concernant votre plateforme."
    );
    return `https://wa.me/${whatsappNumber}?text=${text}`;
  }, [whatsappNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      return "Veuillez entrer votre nom complet.";
    }
    if (!formData.email.trim()) {
      return "Veuillez entrer votre adresse email.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return "Veuillez entrer une adresse email valide.";
    }
    if (!formData.message.trim()) {
      return "Veuillez écrire votre message.";
    }
    if (formData.message.trim().length < 10) {
      return "Votre message est trop court.";
    }
    return null;
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      subject: "question",
      orderRef: "",
      message: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: "", text: "" });

    const error = validateForm();
    if (error) {
      setAlert({ type: "danger", text: error });
      return;
    }

    try {
      setLoading(true);

      await addDoc(collection(db, "support_messages"), {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        subject: formData.subject,
        orderRef: formData.orderRef.trim(),
        message: formData.message.trim(),
        status: "nouveau",
        source: "site_web",
        createdAt: serverTimestamp(),
      });

      setAlert({
        type: "success",
        text: "Votre message a bien été envoyé. Notre équipe vous répondra bientôt.",
      });

      resetForm();
    } catch (error) {
      console.error("Erreur envoi support :", error);
      setAlert({
        type: "danger",
        text: "Une erreur est survenue lors de l’envoi. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact & Support | GoShop</title>
        <meta
          name="description"
          content="Contactez GoShop pour poser vos questions, demander de l’aide ou obtenir un support rapide."
        />
      </Helmet>

      <div className="bg-light min-vh-100">
        {/* HERO */}
        <section className="py-5 border-bottom bg-white">
          <div className="container">
            <div className="row align-items-center g-4">
              <div className="col-lg-7">
                <span className="badge bg-primary-subtle text-primary px-3 py-2 mb-3 rounded-pill">
                  GoShop Support
                </span>
                <h1 className="fw-bold display-6 mb-3">
                  Besoin d’aide ? Contactez-nous facilement
                </h1>
                <p className="text-muted fs-5 mb-4">
                  Une question sur une commande, un produit, un vendeur ou
                  l’utilisation de la plateforme ? Notre équipe est là pour vous
                  aider rapidement.
                </p>

                <div className="d-flex flex-wrap gap-3">
                  <a href={whatsappLink} target="_blank" rel="noreferrer" className="btn btn-success btn-lg rounded-pill px-4">
                    WhatsApp
                  </a>
                  <a href={`mailto:${supportEmail}`} className="btn btn-outline-dark btn-lg rounded-pill px-4">
                    Envoyer un email
                  </a>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">Nos canaux de contact</h5>

                    <div className="mb-3">
                      <div className="fw-semibold">Email</div>
                      <a href={`mailto:${supportEmail}`} className="text-decoration-none">
                        {supportEmail}
                      </a>
                    </div>

                    <div className="mb-3">
                      <div className="fw-semibold">Téléphone</div>
                      <a href={`tel:${supportPhone}`} className="text-decoration-none">
                        {supportPhone}
                      </a>
                    </div>

                    <div className="mb-0">
                      <div className="fw-semibold">WhatsApp</div>
                      <a href={whatsappLink} target="_blank" rel="noreferrer" className="text-decoration-none">
                        Discuter sur WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTENT */}
        <section className="py-5">
          <div className="container">
            <div className="row g-4">
              {/* FORMULAIRE */}
              <div className="col-lg-7">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4 p-md-5">
                    <h2 className="h4 fw-bold mb-3">Envoyer une demande</h2>
                    <p className="text-muted mb-4">
                      Remplissez ce formulaire et nous vous répondrons dans les
                      meilleurs délais.
                    </p>

                    {alert.text && (
                      <div className={`alert alert-${alert.type} rounded-3`} role="alert">
                        {alert.text}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Nom complet</label>
                          <input
                            type="text"
                            name="fullName"
                            className="form-control form-control-lg"
                            placeholder="Votre nom complet"
                            value={formData.fullName}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Email</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control form-control-lg"
                            placeholder="exemple@email.com"
                            value={formData.email}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Téléphone</label>
                          <input
                            type="text"
                            name="phone"
                            className="form-control form-control-lg"
                            placeholder="+228 XX XX XX XX"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Sujet</label>
                          <select
                            name="subject"
                            className="form-select form-select-lg"
                            value={formData.subject}
                            onChange={handleChange}
                          >
                            <option value="question">Question générale</option>
                            <option value="commande">Problème de commande</option>
                            <option value="paiement">Paiement</option>
                            <option value="livraison">Livraison</option>
                            <option value="vendeur">Problème avec un vendeur</option>
                            <option value="compte">Compte utilisateur</option>
                            <option value="assistance">Besoin d’assistance</option>
                            <option value="autre">Autre</option>
                          </select>
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-semibold">
                            Référence de commande (optionnel)
                          </label>
                          <input
                            type="text"
                            name="orderRef"
                            className="form-control form-control-lg"
                            placeholder="Ex: CMD-2026-00125"
                            value={formData.orderRef}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-semibold">Message</label>
                          <textarea
                            name="message"
                            rows="6"
                            className="form-control"
                            placeholder="Expliquez votre problème ou votre question..."
                            value={formData.message}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-12 d-grid d-sm-flex justify-content-sm-end">
                          <button
                            type="submit"
                            className="btn btn-primary btn-lg rounded-pill px-4"
                            disabled={loading}
                          >
                            {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* INFOS + FAQ */}
              <div className="col-lg-5">
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold mb-3">Comment pouvons-nous vous aider ?</h3>
                    <div className="d-flex flex-column gap-3">
                      <div className="p-3 rounded-3 bg-light">
                        <div className="fw-semibold">Suivi de commande</div>
                        <small className="text-muted">
                          Assistance sur vos achats, statuts et livraisons.
                        </small>
                      </div>

                      <div className="p-3 rounded-3 bg-light">
                        <div className="fw-semibold">Paiement et transaction</div>
                        <small className="text-muted">
                          Aide en cas d’échec, de doute ou de vérification.
                        </small>
                      </div>

                      <div className="p-3 rounded-3 bg-light">
                        <div className="fw-semibold">Support vendeur</div>
                        <small className="text-muted">
                          Questions liées aux produits, boutiques et publication.
                        </small>
                      </div>

                      <div className="p-3 rounded-3 bg-light">
                        <div className="fw-semibold">Aide technique</div>
                        <small className="text-muted">
                          Problèmes d’accès, d’affichage ou d’utilisation du site.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4">
                    <h3 className="h5 fw-bold mb-3">Questions fréquentes</h3>

                    <div className="accordion" id="faqSupport">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#faqOne"
                          >
                            Comment contacter GoShop rapidement ?
                          </button>
                        </h2>
                        <div
                          id="faqOne"
                          className="accordion-collapse collapse show"
                          data-bs-parent="#faqSupport"
                        >
                          <div className="accordion-body">
                            Vous pouvez nous écrire via le formulaire, par email
                            ou directement sur WhatsApp.
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#faqTwo"
                          >
                            Puis-je demander de l’aide pour une commande ?
                          </button>
                        </h2>
                        <div
                          id="faqTwo"
                          className="accordion-collapse collapse"
                          data-bs-parent="#faqSupport"
                        >
                          <div className="accordion-body">
                            Oui. Indiquez simplement votre référence de commande
                            et décrivez le problème dans votre message.
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#faqThree"
                          >
                            En combien de temps recevez-vous une réponse ?
                          </button>
                        </h2>
                        <div
                          id="faqThree"
                          className="accordion-collapse collapse"
                          data-bs-parent="#faqSupport"
                        >
                          <div className="accordion-body">
                            Nous faisons le maximum pour répondre le plus vite
                            possible selon le type de demande.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* END RIGHT */}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}